import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Box, Button, Checkbox, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TextField, Typography } from '@mui/material';
import { Icon, InputWithIcon, toast } from '@livable/saas-react-components';
import useStyles from './styles';
import { useStore } from 'src/stores';
import { BillActionsMenu } from './BillActionsMenu';
import { ConfirmActionModal } from './BillDetails/ConfirmActionModal';
import { BillsFilterMenu } from './BillsFilterMenu';
import { UploadBillModal } from './UploadBillModal';
import { UploadedBillDetailsModal } from './UploadedBillDetailsModal';
import { BILL_STATUSES, getPropertyAddressIndex, mapCompanyBillToRow } from './companyBillRows';
import { EMPTY_BILL_FILTERS, getBillFilterOptions, hasActiveBillFilters, matchesBillFilters } from './billsFilters';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const DEFAULT_ROWS_PER_PAGE = 25;
const POLL_INTERVAL_MS = 5000;
function formatUsd(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}

/**
 * Progress columns count what is still outstanding, not what is finished. Once nothing is left the
 * column collapses to a single success tag ("Allocated", "Pushed") instead of "0/n Pending".
 */
function progressChipProps(progress, doneLabel) {
  var _progress$total, _progress$done;
  const of = (_progress$total = progress === null || progress === void 0 ? void 0 : progress.total) !== null && _progress$total !== void 0 ? _progress$total : 0;
  const pending = Math.max(of - ((_progress$done = progress === null || progress === void 0 ? void 0 : progress.done) !== null && _progress$done !== void 0 ? _progress$done : 0), 0);
  if (of > 0 && pending === 0) return {
    label: doneLabel,
    variant: 'success'
  };
  return {
    label: "".concat(pending, "/").concat(of, " Pending"),
    variant: 'inactive'
  };
}

/** Same HTML for every plain-text table cell: div.MuiTypography (body2, primary). */
function BillCellText(_ref) {
  let children = _ref.children,
    _ref$noWrap = _ref.noWrap,
    noWrap = _ref$noWrap === void 0 ? false : _ref$noWrap,
    title = _ref.title,
    sx = _ref.sx;
  const nowrapSx = noWrap ? {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    maxWidth: '100%',
    minWidth: 0
  } : {};
  return /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.primary",
    component: "div",
    noWrap: noWrap,
    title: title,
    sx: _objectSpread(_objectSpread({}, nowrapSx), sx)
  }, children);
}

/** System Chip variants: inactive, info, warning, success, critical */
function billStatusChipVariant(status) {
  switch (status) {
    case BILL_STATUSES.validated:
      return 'success';
    case BILL_STATUSES.extracted:
      return 'info';
    case BILL_STATUSES.processing:
    case BILL_STATUSES.needsProperty:
      return 'warning';
    case BILL_STATUSES.failed:
      return 'critical';
    default:
      return 'inactive';
  }
}
function getSortComparable(row, orderBy) {
  var _row$fileName, _row$status, _row$allocation$done, _row$allocation, _row$allocation$total, _row$allocation2, _row$appfolioPush$don, _row$appfolioPush, _row$appfolioPush$tot, _row$appfolioPush2, _row$billSource, _row$utilityIcons$len, _row$utilityIcons, _row$propertiesLines, _row$account, _row$provider, _row$servicePeriod, _row$days, _row$amount, _row$invoiceNumber, _row$dueDate, _row$dateUploaded;
  switch (orderBy) {
    case 'fileName':
      return (_row$fileName = row.fileName) !== null && _row$fileName !== void 0 ? _row$fileName : '';
    case 'status':
      return (_row$status = row.status) !== null && _row$status !== void 0 ? _row$status : '';
    case 'allocation':
      return ((_row$allocation$done = (_row$allocation = row.allocation) === null || _row$allocation === void 0 ? void 0 : _row$allocation.done) !== null && _row$allocation$done !== void 0 ? _row$allocation$done : 0) / Math.max((_row$allocation$total = (_row$allocation2 = row.allocation) === null || _row$allocation2 === void 0 ? void 0 : _row$allocation2.total) !== null && _row$allocation$total !== void 0 ? _row$allocation$total : 1, 1);
    case 'appfolioPush':
      return ((_row$appfolioPush$don = (_row$appfolioPush = row.appfolioPush) === null || _row$appfolioPush === void 0 ? void 0 : _row$appfolioPush.done) !== null && _row$appfolioPush$don !== void 0 ? _row$appfolioPush$don : 0) / Math.max((_row$appfolioPush$tot = (_row$appfolioPush2 = row.appfolioPush) === null || _row$appfolioPush2 === void 0 ? void 0 : _row$appfolioPush2.total) !== null && _row$appfolioPush$tot !== void 0 ? _row$appfolioPush$tot : 1, 1);
    case 'billSource':
      return (_row$billSource = row.billSource) !== null && _row$billSource !== void 0 ? _row$billSource : '';
    case 'utilities':
      return (_row$utilityIcons$len = (_row$utilityIcons = row.utilityIcons) === null || _row$utilityIcons === void 0 ? void 0 : _row$utilityIcons.length) !== null && _row$utilityIcons$len !== void 0 ? _row$utilityIcons$len : 0;
    case 'properties':
      return ((_row$propertiesLines = row.propertiesLines) !== null && _row$propertiesLines !== void 0 ? _row$propertiesLines : []).join(' ');
    case 'account':
      return (_row$account = row.account) !== null && _row$account !== void 0 ? _row$account : '';
    case 'provider':
      return (_row$provider = row.provider) !== null && _row$provider !== void 0 ? _row$provider : '';
    case 'servicePeriod':
      return (_row$servicePeriod = row.servicePeriod) !== null && _row$servicePeriod !== void 0 ? _row$servicePeriod : '';
    case 'days':
      return (_row$days = row.days) !== null && _row$days !== void 0 ? _row$days : 0;
    case 'amount':
      return (_row$amount = row.amount) !== null && _row$amount !== void 0 ? _row$amount : 0;
    case 'invoiceNumber':
      return (_row$invoiceNumber = row.invoiceNumber) !== null && _row$invoiceNumber !== void 0 ? _row$invoiceNumber : '';
    case 'dueDate':
      return (_row$dueDate = row.dueDate) !== null && _row$dueDate !== void 0 ? _row$dueDate : '';
    case 'dateUploaded':
      return (_row$dateUploaded = row.dateUploaded) !== null && _row$dateUploaded !== void 0 ? _row$dateUploaded : '';
    default:
      return null;
  }
}
export const BillsTab = observer(() => {
  const classes = useStyles();
  const store = useStore();
  const isAppfolio = store.company.companyFeatureStore.isAppfolio;
  const addressById = getPropertyAddressIndex(store.company.propertiesList);
  const rows = store.companyBills.items.map(bill => mapCompanyBillToRow(bill, addressById));
  useEffect(() => {
    store.companyBills.getCompanyBillsAsync.run({}, []);
  }, [store.companyBills]);
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    orderBy = _useState2[0],
    setOrderBy = _useState2[1];
  const _useState3 = useState('asc'),
    _useState4 = _slicedToArray(_useState3, 2),
    order = _useState4[0],
    setOrder = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    page = _useState6[0],
    setPage = _useState6[1];
  const _useState7 = useState(DEFAULT_ROWS_PER_PAGE),
    _useState8 = _slicedToArray(_useState7, 2),
    rowsPerPage = _useState8[0],
    setRowsPerPage = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    selected = _useState0[0],
    setSelected = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    searchQuery = _useState10[0],
    setSearchQuery = _useState10[1];
  const _useState11 = useState(EMPTY_BILL_FILTERS),
    _useState12 = _slicedToArray(_useState11, 2),
    filters = _useState12[0],
    setFilters = _useState12[1];
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isUploadBillModalOpen = _useState14[0],
    setIsUploadBillModalOpen = _useState14[1];
  const _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    selectedBillId = _useState16[0],
    setSelectedBillId = _useState16[1];
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    rowToDelete = _useState18[0],
    setRowToDelete = _useState18[1];
  const _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  const selectedBill = selectedBillId ? store.uploadedBills.bills.find(bill => bill.billId === selectedBillId) : null;

  /**
   * Extraction keeps moving bills along in the background, so the list is refreshed while any of
   * them is still on its way. It rests while the details view is open: that view reads its own bill,
   * and the whole list is worth reading again once, on the way out.
   */
  const hasWorkInFlight = store.companyBills.hasBillsInProgress && !selectedBillId;

  /**
   * Refreshes are chained instead of scheduled on a fixed interval: `run` cancels the request that
   * is still in flight, so a tick landing mid-pass would abort it. The gap is measured from the end
   * of one pass to the next.
   */
  useEffect(() => {
    if (!hasWorkInFlight) return undefined;
    let isActive = true;
    let timeoutId = null;
    const poll = async () => {
      await store.companyBills.getCompanyBillsAsync.run({}, []);
      if (isActive) timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [hasWorkInFlight, store.companyBills]);

  /**
   * The one bill being looked at, read as soon as it is opened — the list carries no extraction
   * payload, and waiting for the next pass over the whole list to fetch it made opening the view
   * feel slow. It is read again while the pipeline still has the bill, and left alone once it lands.
   */
  useEffect(() => {
    if (!selectedBillId) return undefined;
    let isActive = true;
    let timeoutId = null;
    const readBill = async () => {
      await store.uploadedBills.pollUploadedBillAsync.run({
        billId: selectedBillId
      });
      if (isActive && store.uploadedBills.isWorthReadingAgain(selectedBillId)) {
        timeoutId = setTimeout(readBill, POLL_INTERVAL_MS);
      }
    };
    readBill();
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedBillId, store.uploadedBills]);

  /**
   * Deep link: notification emails point at /bills?billId={id}. The bill is registered in the
   * session store the same way "View details" does, and the details effect above then reads it by
   * id — so the link works even before the list has loaded the row. Closing the details view clears
   * the param, so a refresh shows the list rather than reopening the bill.
   */
  const deepLinkBillId = searchParams.get('billId');
  useEffect(() => {
    if (!deepLinkBillId) return;
    if (!store.uploadedBills.bills.some(bill => bill.billId === deepLinkBillId)) {
      store.uploadedBills.addUploadedBill({
        billId: deepLinkBillId
      });
    }
    setSelectedBillId(deepLinkBillId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkBillId]);

  /**
   * The list endpoint carries no extraction payload, so the details view is backed by the session
   * store. A bill that was not uploaded in this session is registered here and read on opening.
   */
  const handleViewDetails = row => {
    if (!store.uploadedBills.bills.some(bill => bill.billId === row.id)) {
      store.uploadedBills.addUploadedBill({
        billId: row.id,
        fileName: row.fileName,
        propertyAddress: (row.propertiesLines || [])[0] || ''
      });
    }
    setSelectedBillId(row.id);
  };

  /** Whatever came of the visit — an allocation, an edit — shows on the row once the list is reread. */
  const handleCloseDetails = () => {
    setSelectedBillId(null);
    if (searchParams.has('billId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('billId');
      setSearchParams(nextParams, {
        replace: true
      });
    }
    store.companyBills.getCompanyBillsAsync.run({}, []);
  };
  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredBySearch = !searchLower ? rows : rows.filter(b => {
    var _b$propertiesLines;
    const blob = [b.fileName, b.status, b.account, b.provider, b.invoiceNumber, b.servicePeriod, b.dueDate, b.dateUploaded, b.billSource, ...((_b$propertiesLines = b.propertiesLines) !== null && _b$propertiesLines !== void 0 ? _b$propertiesLines : [])].filter(Boolean).join(' ').toLowerCase();
    return blob.includes(searchLower);
  });
  const filterOptions = getBillFilterOptions(rows);
  const filteredByColumns = filteredBySearch.filter(row => matchesBillFilters(row, filters));
  const sortedRows = orderBy == null ? [...filteredByColumns] : [...filteredByColumns].sort((a, b) => {
    const aVal = getSortComparable(a, orderBy);
    const bVal = getSortComparable(b, orderBy);
    const cmp = typeof aVal === 'string' ? (aVal || '').localeCompare(bVal || '') : (aVal !== null && aVal !== void 0 ? aVal : 0) - (bVal !== null && bVal !== void 0 ? bVal : 0);
    return order === 'asc' ? cmp : -cmp;
  });
  const start = page * rowsPerPage;
  const paginatedRows = sortedRows.slice(start, start + rowsPerPage);
  const handleSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelected(paginatedRows.map(r => r.id));
    } else {
      setSelected([]);
    }
  };
  const handleSelectOne = id => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = e => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  const handleClearSelection = () => setSelected([]);
  const forgetRow = id => {
    store.uploadedBills.removeUploadedBill(id);
    setSelected(prev => prev.filter(selectedId => selectedId !== id));
    if (selectedBillId === id) setSelectedBillId(null);
  };

  /**
   * Deleting a bill cannot be taken back, so it is confirmed first. The service has the last word —
   * it refuses a bill whose services have been allocated and says which, which is worth reading out.
   */
  const handleDeleteRow = async row => {
    setRowToDelete(null);
    const resp = await store.companyBills.deleteBillAsync.run({
      billId: row.id
    });
    if (!resp) return;
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not deleted',
        body: [resp.message || 'This bill has been allocated and can no longer be deleted.']
      });
      return;
    }
    forgetRow(row.id);
    toast('success', {
      body: ["".concat(row.fileName, " deleted.")]
    });
  };
  const hasSelection = selected.length > 0;
  const hasActiveFilters = Boolean(searchLower) || hasActiveBillFilters(filters);
  /** An empty table on first load reads as "no bills", so it waits for the first pass to come back. */
  const isLoadingBills = store.companyBills.getCompanyBillsAsync.inProgress;
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.billsRoot,
    style: hasSelection ? {
      paddingBottom: 56
    } : undefined
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbar
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbarLeft
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    size: "small",
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    placeholder: "Search bill",
    fullWidth: true,
    value: searchQuery,
    onChange: handleSearchChange
  })), /*#__PURE__*/React.createElement(BillsFilterMenu, {
    filters: filters,
    onChange: next => {
      setFilters(next);
      setPage(0);
    },
    options: filterOptions
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    onClick: () => setIsUploadBillModalOpen(true),
    'custom-attribute': 'bills-upload-bill'
  }, "Upload bill")), /*#__PURE__*/React.createElement(Paper, {
    variant: "outlined",
    className: classes.billsTablePaper
  }, filteredByColumns.length === 0 && !isLoadingBills ? /*#__PURE__*/React.createElement(Box, {
    className: classes.billsTableEmpty
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, hasActiveFilters ? 'No bills match your search and filters.' : 'No bills uploaded yet.')) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TableContainer, {
    className: classes.billsTableContainer
  }, /*#__PURE__*/React.createElement(Table, {
    stickyHeader: true,
    size: "small",
    className: classes.billsTable,
    "aria-label": "bills table"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    padding: "checkbox",
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsStickyCheckboxHead)
  }, /*#__PURE__*/React.createElement(Checkbox, {
    indeterminate: selected.length > 0 && selected.length < paginatedRows.length,
    checked: paginatedRows.length > 0 && selected.length === paginatedRows.length,
    onChange: handleSelectAll,
    size: "small"
  })), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsFileNameCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'fileName',
    direction: orderBy === 'fileName' ? order : 'asc',
    onClick: () => handleSort('fileName')
  }, "File name")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'status',
    direction: orderBy === 'status' ? order : 'asc',
    onClick: () => handleSort('status')
  }, "Status")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'allocation',
    direction: orderBy === 'allocation' ? order : 'asc',
    onClick: () => handleSort('allocation')
  }, "Allocation")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'billSource',
    direction: orderBy === 'billSource' ? order : 'asc',
    onClick: () => handleSort('billSource')
  }, "Bill source")), isAppfolio ? /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'appfolioPush',
    direction: orderBy === 'appfolioPush' ? order : 'asc',
    onClick: () => handleSort('appfolioPush')
  }, "Appfolio push")) : null, /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, "Utilities"), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'properties',
    direction: orderBy === 'properties' ? order : 'asc',
    onClick: () => handleSort('properties')
  }, "Properties")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'account',
    direction: orderBy === 'account' ? order : 'asc',
    onClick: () => handleSort('account')
  }, "Account")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'provider',
    direction: orderBy === 'provider' ? order : 'asc',
    onClick: () => handleSort('provider')
  }, "Provider")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'servicePeriod',
    direction: orderBy === 'servicePeriod' ? order : 'asc',
    onClick: () => handleSort('servicePeriod')
  }, "Service period")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'days',
    direction: orderBy === 'days' ? order : 'asc',
    onClick: () => handleSort('days')
  }, "Days")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'amount',
    direction: orderBy === 'amount' ? order : 'asc',
    onClick: () => handleSort('amount')
  }, "Amount")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'invoiceNumber',
    direction: orderBy === 'invoiceNumber' ? order : 'asc',
    onClick: () => handleSort('invoiceNumber')
  }, "Invoice #")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'dueDate',
    direction: orderBy === 'dueDate' ? order : 'asc',
    onClick: () => handleSort('dueDate')
  }, "Due")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'dateUploaded',
    direction: orderBy === 'dateUploaded' ? order : 'asc',
    onClick: () => handleSort('dateUploaded')
  }, "Date uploaded")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsStickyActionsHead),
    align: "center"
  }, "Actions"))), /*#__PURE__*/React.createElement(TableBody, null, paginatedRows.map(row => {
    var _row$utilityTypes, _row$utilityIcons2, _row$propertiesLines2;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: row.id,
      hover: true,
      className: "".concat(classes.tableBodyRow, " ").concat(classes.billsClickableRow),
      onClick: () => handleViewDetails(row),
      selected: selected.includes(row.id)
    }, /*#__PURE__*/React.createElement(TableCell, {
      padding: "checkbox",
      className: classes.billsStickyCheckbox,
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: selected.includes(row.id),
      onChange: () => handleSelectOne(row.id),
      size: "small"
    })), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsFileNameCell, " ").concat(classes.billsCellNoWrap)
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.billsFileNameRow
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "file-text",
      state: "grey"
    }), /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.fileName
    }, row.fileName))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(Chip, {
      className: classes.billsTableChip,
      label: row.status,
      size: "small",
      title: row.statusTooltip || row.status,
      variant: billStatusChipVariant(row.status)
    })), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(Chip, _extends({
      className: classes.billsTableChip,
      size: "small"
    }, progressChipProps(row.allocation, 'Allocated')))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(Chip, {
      className: classes.billsTableChip,
      label: row.billSource,
      size: "small",
      variant: "inactive"
    })), isAppfolio ? /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(Chip, _extends({
      className: classes.billsTableChip,
      size: "small"
    }, progressChipProps(row.appfolioPush, 'Pushed')))) : null, /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsUtilitiesCell, " ").concat(classes.billsCellNoWrap)
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.billsUtilitiesIcons,
      title: ((_row$utilityTypes = row.utilityTypes) !== null && _row$utilityTypes !== void 0 ? _row$utilityTypes : []).join(', ')
    }, ((_row$utilityIcons2 = row.utilityIcons) !== null && _row$utilityIcons2 !== void 0 ? _row$utilityIcons2 : []).map((type, uIdx) => /*#__PURE__*/React.createElement(Icon, {
      key: "".concat(row.id, "-util-").concat(uIdx),
      type: type,
      state: "grey"
    })))), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsPropertiesCell, " ").concat(classes.billsCellNoWrap)
    }, ((_row$propertiesLines2 = row.propertiesLines) !== null && _row$propertiesLines2 !== void 0 ? _row$propertiesLines2 : []).map((line, i) => /*#__PURE__*/React.createElement(BillCellText, {
      key: i,
      noWrap: true,
      title: line,
      sx: {
        lineHeight: 1.35
      }
    }, line))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.account
    }, row.account)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.provider
    }, row.provider)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.servicePeriod
    }, row.servicePeriod)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: String(row.days)
    }, row.days)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: formatUsd(row.amount)
    }, formatUsd(row.amount))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.invoiceNumber
    }, row.invoiceNumber)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.dueDate
    }, row.dueDate)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.dateUploaded
    }, row.dateUploaded)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.actionsCell, " ").concat(classes.billsStickyActions),
      align: "center",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement(BillActionsMenu, {
      canDelete: row.canDelete,
      onDelete: () => setRowToDelete(row),
      onViewDetails: () => handleViewDetails(row)
    })));
  })))), /*#__PURE__*/React.createElement(TablePagination, {
    component: "div",
    count: sortedRows.length,
    page: page,
    onPageChange: handleChangePage,
    rowsPerPage: rowsPerPage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    className: classes.tablePagination
  }))), hasSelection && /*#__PURE__*/React.createElement(Paper, {
    className: classes.selectionBar,
    elevation: 2
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.selectionBarLeft
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleClearSelection,
    "aria-label": "Clear selection"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, selected.length, " items selected")), /*#__PURE__*/React.createElement(Box, {
    className: classes.selectionBarRight
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "tag"
    }),
    onClick: () => console.log('')
  }, "Manage pricings"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }),
    onClick: () => console.log('')
  }, "Change status"))), /*#__PURE__*/React.createElement(UploadBillModal, {
    isOpen: isUploadBillModalOpen,
    handleClose: () => setIsUploadBillModalOpen(false)
  }), /*#__PURE__*/React.createElement(UploadedBillDetailsModal, {
    bill: selectedBill,
    handleClose: handleCloseDetails
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Delete bill",
    isOpen: Boolean(rowToDelete),
    message: "".concat((rowToDelete === null || rowToDelete === void 0 ? void 0 : rowToDelete.fileName) || 'This bill', " will be deleted, along with everything read from it. This cannot be undone."),
    onClose: () => setRowToDelete(null),
    onConfirm: () => handleDeleteRow(rowToDelete),
    title: "Delete bill?"
  }));
});